function placeMarkersIntoGridArray(
  markers: Array<App.SearchMapMarker>,
  gridSize: number,
  projection: google.maps.Projection,
  scale: number,
): Record<string, Cluster> {
  const markerGrid: Record<string, Cluster> = {}

  for (const marker of markers) {
    const { latitude, longitude } = marker
    const markerPosition = projection.fromLatLngToPoint({ lat: latitude, lng: longitude })
    if (!markerPosition) {
      continue
    }
    const pixelCoordinate = new google.maps.Point(Math.floor(((markerPosition.x) * scale)), Math.floor((markerPosition.y) * scale))
    const { x, y } = {
      x: Math.floor(pixelCoordinate.x / gridSize),
      y: Math.floor(pixelCoordinate.y / gridSize),
    }
    const gridKey = `${x}-${y}`

    if (!markerGrid[gridKey]) {
      markerGrid[gridKey] = {
        x,
        y,
        markers: [marker],
      }
    } else {
      markerGrid[gridKey].markers.push(marker)
    }
  }

  return markerGrid
}

interface Cluster {
  x: number
  y: number
  markers: Array<App.SearchMapMarker>
}

export function clusterMarkersByGrid(markers: Array<App.SearchMapMarker>, gridSize = 60, map: google.maps.Map): Record<string, Cluster> {
  if (!map) {
    return {}
  }

  const projection = map.getProjection()
  const zoom = map.getZoom()
  if (!projection || !zoom) {
    return {}
  }

  const scale = 1 << zoom

  const clusters = placeMarkersIntoGridArray(markers, gridSize, projection, scale)

  return clusters
}

interface MarkerPixelCoordinate {
  marker: App.SearchMapMarker
  x: number
  y: number
}

export function clusterMarkersByProximity(markers: Array<App.SearchMapMarker>, maximumProximity = 60, map: google.maps.Map): Record<string, Cluster> {
  if (!map) {
    return {}
  }

  const clusters: Record<string, Cluster> = {}

  const projection = map.getProjection()
  const zoom = map.getZoom()
  if (!projection || !zoom) {
    return {}
  }

  const scale = 1 << zoom

  const sortedMarkers: Array<MarkerPixelCoordinate> = markers.reduce((acc: Array<MarkerPixelCoordinate>, marker) => {
    const { latitude, longitude } = marker
    const markerPosition = projection.fromLatLngToPoint({ lat: latitude, lng: longitude })

    if (markerPosition) {
      const pixelCoordinate = new google.maps.Point(Math.floor((markerPosition.x * scale)), Math.floor(markerPosition.y * scale))
      acc.push({
        marker,
        x: pixelCoordinate.x,
        y: pixelCoordinate.y,
      })
    }

    return acc
  }, []).sort((a, b) => a.x - b.x)

  const clusteredMarkers = new Set()

  sortedMarkers.forEach((marker, index) => {
    if (clusteredMarkers.has(marker.marker.id)) {
      return
    }
    const gridKey = `${marker.x}-${marker.y}`
    if (!clusters[gridKey]) {
      clusters[gridKey] = {
        x: marker.x,
        y: marker.y,
        markers: [marker.marker],
      }
      clusteredMarkers.add(marker.marker.id)
    }
    for (let i = index + 1; i < sortedMarkers.length; i++) {
      const neighbour = sortedMarkers[i]
      if (clusteredMarkers.has(neighbour.marker.id)) {
        continue
      }
      const xDiff = Math.abs(neighbour.x - marker.x)
      const yDiff = Math.abs(neighbour.y - marker.y)
      const distance = Math.sqrt(Math.pow(xDiff, 2) + Math.pow(yDiff, 2))
      if (distance <= maximumProximity) {
        clusteredMarkers.add(neighbour.marker.id)
        clusters[gridKey].markers.push(neighbour.marker)
      } else if (xDiff > maximumProximity) {
        // since markers are sorted by x, we can break early if the x difference is greater than the maximum proximity
        break
      }
    }
  })

  return clusters
}
